import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { LABEL_TEMPLATES } from '../../utils/printUtils';

/**
 * Packing Slip Component (ใบปะหน้าสำหรับออเดอร์)
 * สำหรับตรวจสอบความถูกต้องในการเตรียมจัดส่ง
 * แสดงรายการสินค้าทั้งหมดพร้อม checklist
 * รองรับทั้งออเดอร์เดียว (order) และหลายออเดอร์ (orders array)
 */
const PackingSlip = ({ 
  order, 
  orders, // หลายออเดอร์
  store, 
  templateType = 'a4',
  editedData = null 
}) => {
  // ตรวจสอบว่ามีหลายออเดอร์หรือไม่
  const isMultipleOrders = Array.isArray(orders) && orders.length > 0;
  const ordersList = isMultipleOrders ? orders : (order ? [order] : []);
  
  // คำนวณยอดรวมทั้งหมด
  const totalAmount = ordersList.reduce((sum, ord) => sum + (ord?.total || 0), 0);
  
  // ใช้ข้อมูลที่แก้ไขแล้ว หรือข้อมูลเดิม (สำหรับออเดอร์เดียว)
  const displayData = editedData || {
    buyerName: order?.buyerName || '',
    buyerPhone: order?.buyerPhone || '',
    shippingAddress: order?.shippingAddress || '',
    notes: order?.notes || ''
  };

  const template = LABEL_TEMPLATES[templateType.toUpperCase()] || LABEL_TEMPLATES.A4;
  const [qrCodeUrl, setQrCodeUrl] = useState(null);

  // Generate QR Code สำหรับเบอร์โทร (สำหรับ Sticker template)
  useEffect(() => {
    const generateQRCode = async () => {
      if (templateType.toLowerCase() !== 'sticker') return;
      
      // ใช้เบอร์โทรจากออเดอร์แรก (สำหรับ sticker มักเป็นออเดอร์เดียว)
      const phoneNumber = isMultipleOrders 
        ? (ordersList[0]?.buyerPhone || displayData.buyerPhone)
        : displayData.buyerPhone;
      
      if (!phoneNumber) return;
      
      try {
        const qrData = `tel:${phoneNumber}`;
        const qrCodeDataURL = await QRCode.toDataURL(qrData, {
          width: 150,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        setQrCodeUrl(qrCodeDataURL);
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    };

    generateQRCode();
  }, [templateType, displayData.buyerPhone, ordersList, isMultipleOrders]);

  // Render ตาม template type
  if (templateType.toLowerCase() === 'sticker') {
    return renderStickerTemplate();
  } else if (templateType.toLowerCase() === 'compact') {
    return renderCompactTemplate();
  } else {
    return renderA4Template();
  }

  // A4 Template (default)
  function renderA4Template() {
    return (
    <div 
      id="packing-slip-print"
      className="print-content"
      style={{
        width: template.width,
        minHeight: template.height,
        margin: '0 auto',
        padding: '1.2cm',
        fontFamily: "'Sarabun', 'Kanit', Arial, sans-serif",
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        color: '#000000',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
        colorAdjust: 'exact'
      }}
    >
      {/* Header Section */}
      <div 
        style={{ 
          backgroundColor: '#e5e7eb',
          padding: '16px',
          marginBottom: '16px',
          border: '1px solid #9ca3af'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            {store?.logo && (
              <div style={{ marginBottom: '8px' }}>
                <img 
                  src={store.logo} 
                  alt={store.name}
                  style={{ 
                    maxHeight: '50px', 
                    maxWidth: '150px', 
                    objectFit: 'contain',
                    display: 'block'
                  }}
                />
              </div>
            )}
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#1f2937', marginBottom: '4px' }}>
              {store?.name || 'ร้านค้า'}
            </div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
              ใบปะหน้าออเดอร์ / Packing Slip
              {isMultipleOrders && (
                <span style={{ fontSize: '12px', fontWeight: '500', marginLeft: '8px' }}>
                  ({ordersList.length} ออเดอร์)
                </span>
              )}
            </div>
          </div>
          <div style={{ textAlign: 'right', minWidth: '150px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
              วันที่พิมพ์:
            </div>
            <div style={{ fontSize: '12px', color: '#1f2937' }}>
              {new Date().toLocaleDateString('th-TH', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
              {new Date().toLocaleTimeString('th-TH', { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Main Table */}
      <table 
        style={{ 
          width: '100%',
          borderCollapse: 'collapse',
          border: '2px solid #000000',
          marginBottom: '16px'
        }}
      >
        <thead>
          <tr style={{ backgroundColor: '#f3f4f6' }}>
            <th 
              style={{ 
                border: '1px solid #000000',
                padding: '10px',
                textAlign: 'center',
                fontWeight: '700',
                fontSize: '12px',
                backgroundColor: '#f3f4f6'
              }}
            >
              ลำดับ
            </th>
            <th 
              style={{ 
                border: '1px solid #000000',
                padding: '10px',
                textAlign: 'center',
                fontWeight: '700',
                fontSize: '12px',
                backgroundColor: '#f3f4f6'
              }}
            >
              Order
            </th>
            <th 
              style={{ 
                border: '1px solid #000000',
                padding: '10px',
                textAlign: 'center',
                fontWeight: '700',
                fontSize: '12px',
                backgroundColor: '#f3f4f6'
              }}
            >
              ชื่อ ผู้รับ
            </th>
            <th 
              style={{ 
                border: '1px solid #000000',
                padding: '10px',
                textAlign: 'center',
                fontWeight: '700',
                fontSize: '12px',
                backgroundColor: '#f3f4f6'
              }}
            >
              เบอร์โทร
            </th>
            <th 
              style={{ 
                border: '1px solid #000000',
                padding: '10px',
                textAlign: 'center',
                fontWeight: '700',
                fontSize: '12px',
                backgroundColor: '#f3f4f6'
              }}
            >
              หมายเหตุ
            </th>
          </tr>
        </thead>
        <tbody>
          {ordersList.map((currentOrder, orderIndex) => {
            const currentDisplayData = isMultipleOrders ? {
              buyerName: currentOrder?.buyerName || '',
              buyerPhone: currentOrder?.buyerPhone || '',
              shippingAddress: currentOrder?.shippingAddress || '',
              notes: currentOrder?.notes || ''
            } : displayData;

            return (
              <tr key={currentOrder?.id || orderIndex}>
                <td 
                  style={{ 
                    border: '1px solid #000000',
                    padding: '10px',
                    textAlign: 'center',
                    fontSize: '12px'
                  }}
                >
                  {orderIndex + 1}
                </td>
                <td 
                  style={{ 
                    border: '1px solid #000000',
                    padding: '10px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}
                >
                  {currentOrder?.orderNumber || '-'}
                </td>
                <td 
                  style={{ 
                    border: '1px solid #000000',
                    padding: '10px',
                    textAlign: 'left',
                    fontSize: '12px'
                  }}
                >
                  {currentDisplayData.buyerName || '-'}
                </td>
                <td 
                  style={{ 
                    border: '1px solid #000000',
                    padding: '10px',
                    textAlign: 'center',
                    fontSize: '12px'
                  }}
                >
                  {currentDisplayData.buyerPhone || '-'}
                </td>
                <td 
                  style={{ 
                    border: '1px solid #000000',
                    padding: '10px',
                    textAlign: 'left',
                    fontSize: '12px'
                  }}
                >
                  {currentDisplayData.notes || '-'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Items List with Checklist - สำหรับแต่ละออเดอร์ */}
      {ordersList.map((currentOrder, orderIndex) => {
        if (!currentOrder?.items || currentOrder.items.length === 0) return null;

        return (
          <div key={`items-${currentOrder?.id || orderIndex}`} style={{ marginBottom: '20px' }}>
            {/* Order Separator for Multiple Orders */}
            {isMultipleOrders && orderIndex > 0 && (
              <div 
                style={{ 
                  marginTop: '16px',
                  marginBottom: '16px',
                  borderTop: '2px solid #9ca3af',
                  borderTopStyle: 'solid'
                }}
              />
            )}

            {/* Order Header */}
            {isMultipleOrders && (
              <div 
                style={{ 
                  marginBottom: '12px',
                  padding: '8px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px'
                }}
              >
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937' }}>
                  ออเดอร์ #{currentOrder?.orderNumber || '-'}
                </div>
              </div>
            )}

            {/* Items Table with Checklist */}
            <div style={{ marginBottom: '16px' }}>
              <div 
                style={{ 
                  fontSize: '14px',
                  fontWeight: '700',
                  color: '#1f2937',
                  marginBottom: '8px'
                }}
              >
                📦 รายการสินค้าและ Checklist
              </div>
              <table 
                style={{ 
                  width: '100%',
                  borderCollapse: 'collapse',
                  border: '2px solid #000000',
                  fontSize: '12px'
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6' }}>
                    <th 
                      style={{ 
                        border: '1px solid #000000',
                        padding: '8px',
                        textAlign: 'center',
                        fontWeight: '700',
                        fontSize: '11px',
                        backgroundColor: '#f3f4f6',
                        width: '8%'
                      }}
                    >
                      ✅
                    </th>
                    <th 
                      style={{ 
                        border: '1px solid #000000',
                        padding: '8px',
                        textAlign: 'left',
                        fontWeight: '700',
                        fontSize: '11px',
                        backgroundColor: '#f3f4f6',
                        width: '40%'
                      }}
                    >
                      สินค้า
                    </th>
                    <th 
                      style={{ 
                        border: '1px solid #000000',
                        padding: '8px',
                        textAlign: 'center',
                        fontWeight: '700',
                        fontSize: '11px',
                        backgroundColor: '#f3f4f6',
                        width: '15%'
                      }}
                    >
                      จำนวนที่สั่ง
                    </th>
                    <th 
                      style={{ 
                        border: '1px solid #000000',
                        padding: '8px',
                        textAlign: 'center',
                        fontWeight: '700',
                        fontSize: '11px',
                        backgroundColor: '#f3f4f6',
                        width: '15%'
                      }}
                    >
                      จำนวนที่เตรียม
                    </th>
                    <th 
                      style={{ 
                        border: '1px solid #000000',
                        padding: '8px',
                        textAlign: 'right',
                        fontWeight: '700',
                        fontSize: '11px',
                        backgroundColor: '#f3f4f6',
                        width: '12%'
                      }}
                    >
                      ราคา
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentOrder.items.map((item, itemIndex) => (
                    <tr key={itemIndex}>
                      <td 
                        style={{ 
                          border: '1px solid #000000',
                          padding: '8px',
                          textAlign: 'center',
                          fontSize: '16px'
                        }}
                      >
                        ☐
                      </td>
                      <td 
                        style={{ 
                          border: '1px solid #000000',
                          padding: '8px',
                          textAlign: 'left',
                          fontSize: '11px',
                          fontWeight: '600',
                          color: '#1f2937'
                        }}
                      >
                        {item.productName}
                      </td>
                      <td 
                        style={{ 
                          border: '1px solid #000000',
                          padding: '8px',
                          textAlign: 'center',
                          fontSize: '11px',
                          color: '#374151'
                        }}
                      >
                        {item.quantity} {item.unit || 'ชิ้น'}
                      </td>
                      <td 
                        style={{ 
                          border: '1px solid #000000',
                          padding: '8px',
                          textAlign: 'center',
                          fontSize: '11px',
                          color: '#6b7280',
                          borderLeft: '1px dashed #d1d5db',
                          borderRight: '1px dashed #d1d5db'
                        }}
                      >
                        _____________
                      </td>
                      <td 
                        style={{ 
                          border: '1px solid #000000',
                          padding: '8px',
                          textAlign: 'right',
                          fontSize: '11px',
                          color: '#374151'
                        }}
                      >
                        ฿{item.subtotal?.toLocaleString() || '0'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot style={{ backgroundColor: '#f9fafb' }}>
                  <tr>
                    <td 
                      colSpan="4" 
                      style={{ 
                        border: '1px solid #000000',
                        padding: '8px',
                        textAlign: 'right',
                        fontSize: '11px',
                        fontWeight: '700',
                        color: '#1f2937',
                        backgroundColor: '#f9fafb'
                      }}
                    >
                      ยอดรวม:
                    </td>
                    <td 
                      style={{ 
                        border: '1px solid #000000',
                        padding: '8px',
                        textAlign: 'right',
                        fontSize: '12px',
                        fontWeight: '700',
                        color: '#1f2937',
                        backgroundColor: '#f9fafb'
                      }}
                    >
                      ฿{currentOrder.total?.toLocaleString() || '0'}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        );
      })}

      {/* Packing Checklist */}
      <div 
        style={{ 
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: '#fefce8',
          border: '2px solid #fde047',
          borderRadius: '0.5rem'
        }}
      >
        <div 
          style={{ 
            fontSize: '14px',
            marginBottom: '10px',
            fontWeight: '700',
            color: '#1f2937'
          }}
        >
          ✅ Checklist การเตรียมจัดส่ง
        </div>
        <div 
          style={{ 
            fontSize: '11px',
            lineHeight: '1.6'
          }}
        >
          <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'start', gap: '8px' }}>
            <span style={{ fontSize: '14px' }}>☐</span>
            <span>ตรวจสอบรายการสินค้าครบถ้วนตามออเดอร์</span>
          </div>
          <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'start', gap: '8px' }}>
            <span style={{ fontSize: '14px' }}>☐</span>
            <span>ตรวจสอบคุณภาพสินค้า (สภาพ, น้ำหนัก, จำนวน)</span>
          </div>
          <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'start', gap: '8px' }}>
            <span style={{ fontSize: '14px' }}>☐</span>
            <span>ห่อ/บรรจุสินค้าอย่างระมัดระวัง</span>
          </div>
          <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'start', gap: '8px' }}>
            <span style={{ fontSize: '14px' }}>☐</span>
            <span>แปะใบส่งของ (Shipping Label) บนกล่อง</span>
          </div>
          <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'start', gap: '8px' }}>
            <span style={{ fontSize: '14px' }}>☐</span>
            <span>ตรวจสอบที่อยู่ผู้รับให้ถูกต้อง</span>
          </div>
          <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'start', gap: '8px' }}>
            <span style={{ fontSize: '14px' }}>☐</span>
            <span>บันทึกเลขพัสดุ (ถ้ามี)</span>
          </div>
          <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'start', gap: '8px' }}>
            <span style={{ fontSize: '14px' }}>☐</span>
            <span>ตรวจสอบข้อมูลการชำระเงิน</span>
          </div>
        </div>
      </div>

      {/* Signature Section */}
      <div 
        style={{ 
          marginTop: '20px',
          marginBottom: '16px',
          paddingTop: '12px',
          borderTop: '1px solid #d1d5db',
          borderTopStyle: 'solid'
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          <div>
            <div 
              style={{ 
                fontSize: '11px',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#374151'
              }}
            >
              ผู้เตรียมสินค้า:
            </div>
            <div 
              style={{ 
                borderBottom: '1px solid #9ca3af',
                borderBottomWidth: '1px',
                borderBottomStyle: 'solid',
                paddingBottom: '4px',
                minHeight: '40px',
                marginBottom: '8px'
              }}
            >
              <div 
                style={{ 
                  fontSize: '10px',
                  marginTop: '8px',
                  color: '#6b7280'
                }}
              >
                (ลายเซ็น)
              </div>
            </div>
            <div 
              style={{ 
                fontSize: '10px',
                marginTop: '4px',
                color: '#4b5563'
              }}
            >
              วันที่: _______________
            </div>
          </div>
          <div>
            <div 
              style={{ 
                fontSize: '11px',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#374151'
              }}
            >
              ผู้ตรวจสอบ:
            </div>
            <div 
              style={{ 
                borderBottom: '1px solid #9ca3af',
                borderBottomWidth: '1px',
                borderBottomStyle: 'solid',
                paddingBottom: '4px',
                minHeight: '40px',
                marginBottom: '8px'
              }}
            >
              <div 
                style={{ 
                  fontSize: '10px',
                  marginTop: '8px',
                  color: '#6b7280'
                }}
              >
                (ลายเซ็น)
              </div>
            </div>
            <div 
              style={{ 
                fontSize: '10px',
                marginTop: '4px',
                color: '#4b5563'
              }}
            >
              วันที่: _______________
            </div>
          </div>
        </div>
      </div>

      {/* Footer Section */}
      <div 
        style={{ 
          backgroundColor: '#e5e7eb',
          padding: '16px',
          marginTop: '16px',
          border: '1px solid #9ca3af'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          {/* Left Column - Summary */}
          {isMultipleOrders && (
            <div style={{ flex: '1', minWidth: '200px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937', marginBottom: '8px' }}>
                สรุปข้อมูล
              </div>
              <div style={{ fontSize: '12px', color: '#374151', marginBottom: '4px' }}>
                <span style={{ fontWeight: '600' }}>จำนวนออเดอร์:</span> {ordersList.length} ออเดอร์
              </div>
            </div>
          )}

          {/* Right Column - Contact Info */}
          <div style={{ flex: '1', minWidth: '200px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937', marginBottom: '8px' }}>
              ข้อมูลติดต่อ
            </div>
            {store?.contact?.phone && (
              <div style={{ fontSize: '12px', color: '#374151', marginBottom: '4px' }}>
                📞 {store.contact.phone}
              </div>
            )}
            {store?.contact?.email && (
              <div style={{ fontSize: '12px', color: '#374151', marginBottom: '4px' }}>
                ✉️ {store.contact.email}
              </div>
            )}
            {store?.contact?.lineId && (
              <div style={{ fontSize: '12px', color: '#374151', marginBottom: '4px' }}>
                💬 LINE: {store.contact.lineId}
              </div>
            )}
            {(!store?.contact?.phone && !store?.contact?.email && !store?.contact?.lineId) && (
              <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                ไม่มีข้อมูลติดต่อ
              </div>
            )}
          </div>
        </div>
        
        {/* Bottom Note */}
        <div 
          style={{ 
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: '1px solid #9ca3af',
            fontSize: '11px',
            color: '#6b7280',
            textAlign: 'center'
          }}
        >
          เอกสารนี้สร้างโดยระบบ Cannabis Market - สำหรับการตรวจสอบและเตรียมจัดส่งสินค้า
        </div>
      </div>
    </div>
  );
  }

  // Sticker Template (10x15cm) - สำหรับแปะกล่อง
  function renderStickerTemplate() {
    // สำหรับ sticker มักเป็นออเดอร์เดียว
    const currentOrder = ordersList[0] || order;
    const currentDisplayData = displayData;

    return (
      <div 
        id="packing-slip-print"
        className="print-content"
        style={{
          width: template.width,
          minHeight: template.height,
          margin: '0 auto',
          padding: '0.5cm',
          fontFamily: "'Sarabun', 'Kanit', Arial, sans-serif",
          backgroundColor: '#ffffff',
          boxSizing: 'border-box',
          color: '#000000',
          border: '1px dashed #ccc',
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact',
          colorAdjust: 'exact'
        }}
      >
        {/* Header - Store Info */}
        <div 
          style={{ 
            marginBottom: '8px',
            paddingBottom: '8px',
            borderBottom: '2px solid #1f2937'
          }}
        >
          {store?.logo && (
            <div style={{ marginBottom: '4px' }}>
              <img 
                src={store.logo} 
                alt={store.name}
                style={{ 
                  maxHeight: '30px', 
                  maxWidth: '80px', 
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </div>
          )}
          <div style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937', marginBottom: '2px' }}>
            {store?.name || 'ร้านค้า'}
          </div>
          <div style={{ fontSize: '10px', color: '#4b5563' }}>
            ใบปะหน้าออเดอร์
          </div>
        </div>

        {/* Order Info */}
        <div 
          style={{ 
            marginBottom: '8px',
            paddingBottom: '6px',
            borderBottom: '1px solid #e5e7eb'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
            <span style={{ fontSize: '9px', color: '#4b5563' }}>ออเดอร์:</span>
            <span style={{ fontSize: '11px', fontWeight: '700', color: '#1f2937' }}>
              #{currentOrder?.orderNumber || '-'}
            </span>
          </div>
        </div>

        {/* Recipient Info */}
        <div 
          style={{ 
            marginBottom: '8px',
            padding: '6px',
            backgroundColor: '#f9fafb',
            border: '1px solid #d1d5db',
            borderRadius: '4px'
          }}
        >
          <div style={{ fontSize: '10px', fontWeight: '700', color: '#1f2937', marginBottom: '4px' }}>
            📦 ผู้รับสินค้า
          </div>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#111827', marginBottom: '3px', lineHeight: '1.3' }}>
            {currentDisplayData.buyerName || '-'}
          </div>
          <div style={{ fontSize: '10px', color: '#374151', marginBottom: '3px' }}>
            📞 {currentDisplayData.buyerPhone || '-'}
          </div>
          {currentDisplayData.shippingAddress && (
            <div style={{ fontSize: '9px', color: '#374151', lineHeight: '1.4' }}>
              {currentDisplayData.shippingAddress.split('\n').map((line, i) => (
                <div key={i} style={{ marginBottom: '1px' }}>{line}</div>
              ))}
            </div>
          )}
        </div>

        {/* QR Code Section */}
        {qrCodeUrl && (
          <div style={{ textAlign: 'center', marginBottom: '8px' }}>
            <div style={{ fontSize: '9px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
              📱 Scan เพื่อโทรหา
            </div>
            <img 
              src={qrCodeUrl} 
              alt="QR Code" 
              style={{ 
                width: '80px', 
                height: '80px', 
                margin: '0 auto',
                display: 'block',
                border: '1px solid #d1d5db',
                borderRadius: '4px'
              }}
            />
            <div style={{ fontSize: '9px', color: '#6b7280', marginTop: '2px' }}>
              {currentDisplayData.buyerPhone || ''}
            </div>
          </div>
        )}

        {/* Notes */}
        {currentDisplayData.notes && (
          <div 
            style={{ 
              marginTop: '6px',
              padding: '4px',
              backgroundColor: '#fef3c7',
              border: '1px solid #fde047',
              borderRadius: '4px',
              fontSize: '9px',
              color: '#78350f',
              lineHeight: '1.3'
            }}
          >
            <strong>หมายเหตุ:</strong> {currentDisplayData.notes}
          </div>
        )}

        {/* Footer - Contact */}
        {store?.contact?.phone && (
          <div 
            style={{ 
              marginTop: '6px',
              paddingTop: '6px',
              borderTop: '1px solid #e5e7eb',
              fontSize: '9px',
              color: '#4b5563',
              textAlign: 'center'
            }}
          >
            📞 {store.contact.phone}
          </div>
        )}
      </div>
    );
  }

  // Compact Template (8x12cm) - แบบย่อ แต่ครบถ้วน
  function renderCompactTemplate() {
    return (
      <div 
        id="packing-slip-print"
        className="print-content"
        style={{
          width: template.width,
          minHeight: template.height,
          margin: '0 auto',
          padding: '0.5cm',
          fontFamily: "'Sarabun', 'Kanit', Arial, sans-serif",
          backgroundColor: '#ffffff',
          boxSizing: 'border-box',
          color: '#000000',
          border: '1px dashed #ccc',
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact',
          colorAdjust: 'exact'
        }}
      >
        {/* Compact Header */}
        <div 
          style={{ 
            marginBottom: '8px',
            paddingBottom: '8px',
            borderBottom: '2px solid #1f2937'
          }}
        >
          {store?.logo && (
            <div style={{ marginBottom: '4px' }}>
              <img 
                src={store.logo} 
                alt={store.name}
                style={{ 
                  maxHeight: '25px', 
                  maxWidth: '70px', 
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </div>
          )}
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937', marginBottom: '2px' }}>
            {store?.name || 'ร้านค้า'}
          </div>
          <div style={{ fontSize: '9px', color: '#4b5563' }}>
            ใบปะหน้าออเดอร์ {isMultipleOrders && `(${ordersList.length} ออเดอร์)`}
          </div>
          <div style={{ fontSize: '8px', color: '#6b7280', marginTop: '2px' }}>
            {new Date().toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' })}
          </div>
        </div>

        {/* Compact Orders Table */}
        <table 
          style={{ 
            width: '100%',
            borderCollapse: 'collapse',
            border: '1px solid #000000',
            fontSize: '8px',
            marginBottom: '8px'
          }}
        >
          <thead>
            <tr style={{ backgroundColor: '#f3f4f6' }}>
              <th 
                style={{ 
                  border: '1px solid #000000',
                  padding: '4px',
                  textAlign: 'center',
                  fontWeight: '700',
                  fontSize: '8px',
                  backgroundColor: '#f3f4f6',
                  width: '12%'
                }}
              >
                ลำดับ
              </th>
              <th 
                style={{ 
                  border: '1px solid #000000',
                  padding: '4px',
                  textAlign: 'center',
                  fontWeight: '700',
                  fontSize: '8px',
                  backgroundColor: '#f3f4f6',
                  width: '25%'
                }}
              >
                Order
              </th>
              <th 
                style={{ 
                  border: '1px solid #000000',
                  padding: '4px',
                  textAlign: 'center',
                  fontWeight: '700',
                  fontSize: '8px',
                  backgroundColor: '#f3f4f6',
                  width: '28%'
                }}
              >
                ชื่อผู้รับ
              </th>
              <th 
                style={{ 
                  border: '1px solid #000000',
                  padding: '4px',
                  textAlign: 'center',
                  fontWeight: '700',
                  fontSize: '8px',
                  backgroundColor: '#f3f4f6',
                  width: '20%'
                }}
              >
                เบอร์โทร
              </th>
              <th 
                style={{ 
                  border: '1px solid #000000',
                  padding: '4px',
                  textAlign: 'center',
                  fontWeight: '700',
                  fontSize: '8px',
                  backgroundColor: '#f3f4f6',
                  width: '15%'
                }}
              >
                หมายเหตุ
              </th>
            </tr>
          </thead>
          <tbody>
            {ordersList.map((currentOrder, orderIndex) => {
              const currentDisplayData = isMultipleOrders ? {
                buyerName: currentOrder?.buyerName || '',
                buyerPhone: currentOrder?.buyerPhone || '',
                shippingAddress: currentOrder?.shippingAddress || '',
                notes: currentOrder?.notes || ''
              } : displayData;

              return (
                <tr key={currentOrder?.id || orderIndex}>
                  <td 
                    style={{ 
                      border: '1px solid #000000',
                      padding: '4px',
                      textAlign: 'center',
                      fontSize: '8px'
                    }}
                  >
                    {orderIndex + 1}
                  </td>
                  <td 
                    style={{ 
                      border: '1px solid #000000',
                      padding: '4px',
                      textAlign: 'center',
                      fontSize: '8px',
                      fontWeight: '600'
                    }}
                  >
                    {currentOrder?.orderNumber || '-'}
                  </td>
                  <td 
                    style={{ 
                      border: '1px solid #000000',
                      padding: '4px',
                      textAlign: 'left',
                      fontSize: '8px'
                    }}
                  >
                    {currentDisplayData.buyerName || '-'}
                  </td>
                  <td 
                    style={{ 
                      border: '1px solid #000000',
                      padding: '4px',
                      textAlign: 'center',
                      fontSize: '8px'
                    }}
                  >
                    {currentDisplayData.buyerPhone || '-'}
                  </td>
                  <td 
                    style={{ 
                      border: '1px solid #000000',
                      padding: '4px',
                      textAlign: 'left',
                      fontSize: '7px',
                      lineHeight: '1.2'
                    }}
                  >
                    {currentDisplayData.notes || '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Compact Summary */}
        {isMultipleOrders && (
          <div 
            style={{ 
              marginBottom: '6px',
              padding: '4px',
              backgroundColor: '#f9fafb',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '8px',
              color: '#374151'
            }}
          >
            <strong>จำนวนออเดอร์:</strong> {ordersList.length} ออเดอร์
          </div>
        )}

        {/* Compact Footer */}
        <div 
          style={{ 
            marginTop: '6px',
            paddingTop: '6px',
            borderTop: '1px solid #e5e7eb',
            fontSize: '7px',
            color: '#6b7280',
            textAlign: 'center',
            lineHeight: '1.3'
          }}
        >
          {store?.contact?.phone && (
            <div>📞 {store.contact.phone}</div>
          )}
          <div style={{ marginTop: '2px' }}>
            Cannabis Market
          </div>
        </div>
      </div>
    );
  }
};

export default PackingSlip;

